import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';

function range(count: number): number[] {
  return Array.from({ length: count }, (_, i) => i);
}

/**
 * Stands in for the tables used on Skills, Users, Rules, System, Usage.
 * `widths` sets the relative column widths so the placeholder reads as the same
 * table it replaces rather than a uniform grid. Set `bordered={false}` when the
 * table already sits inside a Card, so the border is not drawn twice.
 */
export function TableSkeleton({
  widths = ['w-40', 'w-20', 'w-28', 'w-64'],
  rows = 6,
  bordered = true,
}: {
  widths?: string[];
  rows?: number;
  bordered?: boolean;
}) {
  return (
    <div className={cn('overflow-hidden', bordered && 'rounded-lg border border-border')}>
      <div className="flex gap-4 border-b bg-muted/30 px-4 py-3">
        {widths.map((w, i) => (
          <Skeleton key={i} className={cn('h-3', w)} />
        ))}
      </div>
      <div className="divide-y">
        {range(rows).map((row) => (
          <div key={row} className="flex items-center gap-4 px-4 py-3.5">
            {widths.map((w, i) => (
              <Skeleton
                key={i}
                className={cn('h-3.5', w)}
                // Stagger the sweep so rows resolve as a wave, not one flat block.
                delayMs={row * 70}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Stand-in for the stat card row on Overview, Usage, and System. */
export function StatCardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <>
      {range(count).map((i) => (
        <Card key={i}>
          <CardContent className="flex items-center gap-3 p-4">
            <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-5 w-12" />
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

/** Stand-in for the agent card grid. */
export function CardGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {range(count).map((i) => (
        <Card key={i}>
          <CardContent className="flex items-start gap-3 p-4">
            <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
            <div className="flex w-full flex-col gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mt-2 h-5 w-16 rounded-full" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Stand-in for the session list on Sessions. */
export function SessionListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-3">
      {range(count).map((i) => (
        <Card key={i}>
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div className="flex w-full flex-col gap-2">
              <Skeleton className="h-4 w-48" delayMs={i * 90} />
              <Skeleton className="h-3 w-72" delayMs={i * 90} />
            </div>
            <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Stand-in for the dispatch/activity feed rows. `itemClassName` mirrors the row
 * padding of the feed being replaced so the skeleton lines up with the real list.
 */
export function ActivityListSkeleton({
  rows = 8,
  itemClassName = 'px-4 py-2.5',
}: {
  rows?: number;
  itemClassName?: string;
}) {
  return (
    <ul className="divide-y">
      {range(rows).map((i) => (
        <li key={i} className={cn('flex items-center gap-3', itemClassName)}>
          <Skeleton className="h-3 w-20 shrink-0" delayMs={i * 70} />
          <Skeleton className="h-3 w-16 shrink-0" delayMs={i * 70} />
          <Skeleton className="h-3 w-16 shrink-0" delayMs={i * 70} />
          <Skeleton className="h-3 flex-1" delayMs={i * 70} />
        </li>
      ))}
    </ul>
  );
}

/** Generic centred spinner-free block for panels without a distinctive shape. */
export function BlockSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn('h-64 w-full rounded-lg', className)} />;
}
